import { CantileverButton } from 'components/CantileverButton'
import SearchBar from 'components/SearchBar'
import {
  KeywordContainer,
  useKeywordManagement,
  useScatterplotContext,
  useTagFiltering,
} from 'features/timeSeries'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry } from 'types'

import { AllTagsTable } from './AllTagsTable'

const AllTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  height: 100%;
`

const SearchContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem;
  flex-shrink: 0;
`

const KeywordRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  padding: 0 0.5rem 0.5rem 0.5rem;
  width: 100%;
  flex-shrink: 0;
`

const FilterModeButton = styled(CantileverButton)`
  max-width: 3rem;
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

interface AllTagsTabProps {
  tags: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
}

export const AllTagsTab: React.FC<AllTagsTabProps> = ({
  tags,
  selectedTags,
}) => {
  const { setSelectedSensors } = useScatterplotContext()

  const {
    tagNameSearch,
    setTagNameSearch,
    tagDescriptionSearch,
    setTagDescriptionSearch,
    tagNameKeywords,
    setTagNameKeywords,
    tagDescriptionKeywords,
    setTagDescriptionKeywords,
    filterMode,
    filteredTags,
    handleToggleFilterMode,
  } = useTagFiltering({ tags })

  const {
    handleRemoveTagNameKeyword,
    handleRemoveTagDescriptionKeyword,
    handleTagNameKeyDown,
    handleTagDescriptionKeyDown,
  } = useKeywordManagement({
    tagNameSearch,
    tagDescriptionSearch,
    tagNameKeywords,
    tagDescriptionKeywords,
    setTagNameKeywords,
    setTagDescriptionKeywords,
    setTagNameSearch,
    setTagDescriptionSearch,
  })

  const handleToggleTag = useCallback(
    (tag: BaseSensorEntry) => {
      setSelectedSensors((prev) => {
        const isSelected = prev.some((t) => t.id === tag.id)
        if (isSelected) {
          return prev.filter((t) => t.id !== tag.id)
        } else {
          return [...prev, tag]
        }
      })
    },
    [setSelectedSensors]
  )

  return (
    <AllTagsContainer>
      <SearchContainer>
        <SearchBar
          placeholder='Search by tag name...'
          value={tagNameSearch}
          onChange={(e) => setTagNameSearch(e.target.value)}
          onKeyDown={handleTagNameKeyDown}
          style={{ flex: 1 }}
        />
        <FilterModeButton onClick={handleToggleFilterMode}>
          {filterMode}
        </FilterModeButton>
        <SearchBar
          placeholder='Search by description...'
          value={tagDescriptionSearch}
          onChange={(e) => setTagDescriptionSearch(e.target.value)}
          onKeyDown={handleTagDescriptionKeyDown}
          style={{ flex: 1 }}
        />
      </SearchContainer>
      <KeywordRow>
        <KeywordContainer
          keywords={tagNameKeywords}
          onRemoveKeyword={handleRemoveTagNameKeyword}
          placeholderText='No saved keywords'
        />
        <KeywordContainer
          keywords={tagDescriptionKeywords}
          onRemoveKeyword={handleRemoveTagDescriptionKeyword}
          placeholderText='No saved keywords'
        />
      </KeywordRow>
      <TableWrapper>
        <AllTagsTable
          filteredTags={filteredTags}
          selectedTags={selectedTags}
          onToggleTag={handleToggleTag}
        />
      </TableWrapper>
    </AllTagsContainer>
  )
}
